import React from 'react';
import { Calendar, TrendingUp, TrendingDown, Clock } from 'lucide-react';
import ProfilePicture from './ProfilePicture';

const MeetingTrendChart = ({ meetings, userType, title = "Meeting Activity", onDayClick }) => {
  // Filter out cancelled meetings
  const activeMeetings = meetings.filter(meeting => meeting.status !== 'cancelled');

  // Helper function to parse date strings as local dates (avoiding timezone issues)
  const parseLocalDate = (dateString) => {
    if (dateString && dateString.includes('-') && dateString.split('-').length === 3) {
      const [year, month, day] = dateString.split('-');
      return new Date(year, month - 1, day); // Month is 0-indexed
    } else {
      return new Date(dateString);
    }
  };

  // Generate next 7 days of data (starting from today)
  const generateWeeklyData = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekData = [];

    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      date.setHours(0, 0, 0, 0);

      const dayMeetings = activeMeetings.filter(meeting => {
        const meetingDate = parseLocalDate(meeting.date);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate.getTime() === date.getTime();
      });

      weekData.push({
        date: date,
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        count: dayMeetings.length,
        meetings: dayMeetings,
        isToday: i === 0
      });
    }

    return weekData;
  };

  // Generate past 7 days for trend comparison
  const generatePastWeekData = () => {
    const today = new Date();
    const pastWeekData = [];

    for (let i = 7; i >= 1; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      date.setHours(0, 0, 0, 0);

      const dayMeetings = activeMeetings.filter(meeting => {
        const meetingDate = parseLocalDate(meeting.date);
        meetingDate.setHours(0, 0, 0, 0);
        return meetingDate.getTime() === date.getTime();
      });

      pastWeekData.push({
        date: date,
        count: dayMeetings.length
      });
    }

    return pastWeekData;
  };

  const weeklyData = generateWeeklyData();
  const pastWeekData = generatePastWeekData();

  // Calculate different time period totals
  const thisWeekTotal = weeklyData.reduce((sum, day) => sum + day.count, 0);

  // Calculate this month's meetings
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const thisMonthTotal = activeMeetings.filter(meeting => {
    const meetingDate = parseLocalDate(meeting.date);
    return meetingDate >= startOfMonth && meetingDate <= endOfMonth;
  }).length;

  // Calculate this year's meetings
  const startOfYear = new Date(now.getFullYear(), 0, 1);
  const endOfYear = new Date(now.getFullYear(), 11, 31);
  const thisYearTotal = activeMeetings.filter(meeting => {
    const meetingDate = parseLocalDate(meeting.date);
    return meetingDate >= startOfYear && meetingDate <= endOfYear;
  }).length;

  // Calculate trend - compare upcoming week to past week
  const pastWeekTotal = pastWeekData.reduce((sum, day) => sum + day.count, 0);
  const trendChange = thisWeekTotal - pastWeekTotal;
  const trendPercentage = pastWeekTotal > 0 ? ((trendChange / pastWeekTotal) * 100).toFixed(1) : 0;

  // Find max value for chart scaling
  const maxCount = Math.max(...weeklyData.map(d => d.count), 1);
  
  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-2">
          <Calendar className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
        </div>
        <div className="flex items-center space-x-2">
          {trendChange > 0 ? (
            <TrendingUp className="w-4 h-4 text-green-500" />
          ) : trendChange < 0 ? (
            <TrendingDown className="w-4 h-4 text-red-500" />
          ) : null}
          <span className={`text-sm font-medium ${
            trendChange > 0 ? 'text-green-600' : 
            trendChange < 0 ? 'text-red-600' : 
            'text-gray-600'
          }`}>
            {trendChange > 0 ? '+' : ''}{trendPercentage}%
          </span>
        </div>
      </div>

      {/* Mini Bar Chart */}
      <div className="mb-4">
        <div className="flex items-end justify-between h-24 space-x-1">
          {weeklyData.map((day, index) => (
            <div key={index} className="flex-1 flex flex-col items-center">
              <div
                className={`w-full rounded-t-sm transition-all duration-200 cursor-pointer relative group ${
                  day.isToday ? 'bg-blue-500 hover:bg-blue-600' : 'bg-blue-200 hover:bg-blue-400'
                } ${day.count > 0 ? 'hover:scale-105' : ''}`}
                style={{
                  height: `${(day.count / maxCount) * 80 + 4}px`,
                  minHeight: '4px'
                }}
                onClick={() => onDayClick && onDayClick(day.date, day.meetings)}
              >
                {/* Tooltip */}
                <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                  {day.count} meeting{day.count !== 1 ? 's' : ''}
                  {day.isToday && ' (Today)'}
                  {onDayClick && day.count > 0 && (
                    <div className="text-gray-300 text-xs">
                      {day.count === 1 ? 'Click to view meeting' : 'Click to view meetings'}
                    </div>
                  )}
                  {onDayClick && day.count === 0 && (
                    <div className="text-gray-300 text-xs">Click to schedule</div>
                  )}
                </div>
              </div>
              <span className={`text-xs mt-2 ${day.isToday ? 'text-blue-600 font-semibold' : 'text-gray-500'}`}>
                {day.day}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4 pt-4 border-t border-gray-200">
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800">{thisWeekTotal}</p>
          <p className="text-xs text-gray-500">This Week</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800">{thisMonthTotal}</p>
          <p className="text-xs text-gray-500">This Month</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-gray-800">{thisYearTotal}</p>
          <p className="text-xs text-gray-500">This Year</p>
        </div>
      </div>

      {/* Detailed Weekly Meetings List */}
      {thisWeekTotal > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-gray-700">Upcoming This Week:</p>
            {/* Color Legend */}
            <div className="flex items-center space-x-4 text-xs text-gray-500">
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 rounded-full bg-blue-500"></div>
                <span>Today</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="w-2 h-2 rounded-full bg-green-400"></div>
                <span>Future</span>
              </div>
            </div>
          </div>
          <div className="space-y-2">
            {weeklyData.filter(day => day.meetings.length > 0).map(day => (
              <div key={day.day} className="space-y-2">
                {day.meetings.map(meeting => {
                  // Format time to 12-hour format
                  const formatTime = (timeString) => {
                    if (!timeString) return '12:00pm';
                    try {
                      // Handle different time formats
                      let time;
                      if (timeString.includes(':')) {
                        const [hours, minutes] = timeString.split(':');
                        time = new Date();
                        time.setHours(parseInt(hours), parseInt(minutes), 0);
                      } else {
                        time = new Date(`1970-01-01T${timeString}`);
                      }
                      return time.toLocaleTimeString('en-US', {
                        hour: 'numeric',
                        minute: '2-digit',
                        hour12: true
                      });
                    } catch (error) {
                      return '12:00pm';
                    }
                  };

                  // Get display name based on user type
                  let customerName;
                  if (userType === 'sales') {
                    customerName = meeting.customer || 'Healthcare Provider';
                  } else {
                    // For customers, try multiple fields for sales rep name
                    customerName = meeting.salesRep ||
                                   meeting.sales_rep_name ||
                                   meeting.salesRepName ||
                                   (meeting.sales_rep ? `${meeting.sales_rep.first_name || ''} ${meeting.sales_rep.last_name || ''}`.trim() : null) ||
                                   'Sales Representative';
                  }

                  // Get restaurant name
                  const restaurantName = meeting.restaurant_name || meeting.restaurant || null;

                  return (
                    <div
                      key={meeting.id}
                      className={`text-xs text-gray-600 ${
                        day.isToday
                          ? 'bg-blue-50 border border-blue-200 rounded-lg p-2 -mx-1'
                          : ''
                      }`}
                    >
                      {/* First line: Day, Sales Rep, and Time */}
                      <div className="flex items-center">
                        <div className="flex items-center w-16">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${day.isToday ? 'bg-blue-500' : 'bg-green-400'}`}></div>
                          <span className="font-medium ml-2">{day.day}:</span>
                        </div>
                        <span className="flex-1 ml-2">{customerName}</span>
                        <div className="flex items-center ml-2 text-gray-500">
                          <Clock className="w-3 h-3 flex-shrink-0" />
                          <span className="ml-1 w-20">{formatTime(meeting.time)}</span>
                        </div>
                      </div>
                      {/* Second line: Restaurant */}
                      {restaurantName && (
                        <div className="ml-[72px] text-gray-500 italic mt-0.5">@ {restaurantName}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No meetings message */}
      {thisWeekTotal === 0 && (
        <div className="mt-4 pt-4 border-t border-gray-200 text-center">
          <p className="text-sm text-gray-500">No meetings scheduled for this week</p>
        </div>
      )}
    </div>
  );
};

export default MeetingTrendChart;